// MCQCard — presentational MCQ renderer used by /admin/eval.
// Sprint 19BG+1 Phase 4 Step 4.
//
// Three modes the card supports:
//   - 'play'      reviewer/student pick mode; no correct/wrong markers
//   - 'review'    after-pick mode; correct option highlighted, picked option's
//                 correctness shown
//   - 'annotate'  blind-review mode for /admin/eval; reviewer reads question +
//                 options to score quality WITHOUT seeing which is correct
//                 (so their score isn't biased by the answer key)
//
// Composition stance — `slot`:
//   The card knows nothing about quality annotation, repair, bookmarking,
//   or flag UI. Pages that need those concerns inject them via the `slot`
//   region beneath the option list. This keeps MCQCard reusable across
//   quiz-view (future migration), admin/eval, and any future surface
//   without prop-drilling onAnnotate / onFlag etc.
//
// Why we don't migrate quiz-view in this step:
//   quiz-view (~625 lines) entangles MCQ rendering with bookmark, EditableField
//   per-option, FlagButton repair-context, and resume-session persistence.
//   Migrating it cleanly is a meaningful blast-radius and is debt-tracked
//   (DEBT-19BG+1-MCQCARD-QUIZVIEW-MIGRATE) for a follow-up sprint. The card
//   is shaped today so quiz-view CAN migrate later without breaking the
//   contract here.

import type { ReactNode } from 'react';
import type { MCQ } from '@examforge/shared/schemas';
import { MarkdownBlock } from '../markdown-block.js';

export type MCQCardMode = 'play' | 'review' | 'annotate';

export interface MCQCardProps {
  item: MCQ;
  mode: MCQCardMode;
  /** 0-3, the index the user has picked. Ignored in 'annotate' mode. */
  selectedIndex?: number | null;
  /** Called when an option is clicked in 'play' mode. */
  onSelect?: (index: number) => void;
  /** Reveal the explanation under the options. Ignored in 'annotate' mode
   *  (annotations are blind by design). */
  showExplanation?: boolean;
  /** Inject extra UI (annotation form, edit / flag controls) below the
   *  option list. The card is unaware of what goes here. */
  slot?: ReactNode;
  /** 19BG+1 Day 5 · MCQCARD-QUIZVIEW-MIGRATE prep: replace the default
   *  question rendering (item.question via MarkdownBlock) with a custom
   *  React node. Used by quiz-view to inject EditableField for inline
   *  question editing. When undefined (default), the card renders item.question
   *  as plain markdown — original behavior unchanged for /admin/eval. */
  questionSlot?: ReactNode;
  /** Optional extra class on the root <article>. Combines with `mcq-card mcq-card-${mode}`. */
  className?: string;
}

const OPTION_LETTERS = ['A', 'B', 'C', 'D'] as const;

/** Strip the `A) ` / `B) ` etc. prefix that schema requires, since we render
 *  the letter chip separately. Falls back to the original text if the prefix
 *  isn't where we expect (defensive: don't crash on malformed data). */
function stripOptionPrefix(opt: string): string {
  return /^[ABCD]\) /.test(opt) ? opt.slice(3) : opt;
}

export function MCQCard({
  item,
  mode,
  selectedIndex = null,
  onSelect,
  showExplanation = false,
  slot,
  questionSlot,
  className,
}: MCQCardProps) {
  const blind = mode === 'annotate';
  const reveal = mode === 'review' && selectedIndex !== null;
  const interactive = mode === 'play';

  return (
    <article
      className={`mcq-card mcq-card-${mode}${className ? ` ${className}` : ''}`}
      data-testid="mcq-card"
    >
      <div className="mcq-card-question markdown-body">
        {questionSlot ?? <MarkdownBlock>{item.question}</MarkdownBlock>}
      </div>

      <ol className="mcq-card-options">
        {item.options.map((opt, i) => {
          const letter = OPTION_LETTERS[i] ?? String.fromCharCode(65 + i);
          const isPicked = selectedIndex === i;
          const isCorrect = i === item.correct;

          // Build the className. In annotate mode we never show is-correct /
          // is-wrong-picked — the reviewer must score blind.
          const stateCls = blind
            ? isPicked ? 'is-picked' : ''
            : reveal
              ? `${isCorrect ? 'is-correct' : ''}${isPicked && !isCorrect ? ' is-wrong-picked' : ''}`.trim()
              : isPicked ? 'is-picked' : '';

          const button = (
            <button
              type="button"
              className={`mcq-card-option ${stateCls}`.trim()}
              onClick={interactive && onSelect ? () => onSelect(i) : undefined}
              disabled={!interactive}
              data-testid={`mcq-card-option-${letter}`}
              data-correct={blind ? undefined : isCorrect ? 'true' : undefined}
            >
              <span className="mcq-card-option-key">{letter}</span>
              <span className="mcq-card-option-text markdown-body">
                <MarkdownBlock>{stripOptionPrefix(opt)}</MarkdownBlock>
              </span>
            </button>
          );

          return <li key={i}>{button}</li>;
        })}
      </ol>

      {showExplanation && !blind && (
        <div className="mcq-card-explanation markdown-body">
          <MarkdownBlock>{item.explanation}</MarkdownBlock>
        </div>
      )}

      {slot ? <div className="mcq-card-slot">{slot}</div> : null}
    </article>
  );
}
